// Catálogo: Sistemas, Categorias e Assuntos — três CRUDs independentes.
//
// Sistema  1 ──< N:N >── Categoria  1 ──< Assunto
//                                        └─ subject_systems (N:N, OPCIONAL)
//
// O assunto herda os sistemas da categoria; `systemIds` só é preenchido quando
// o assunto vale para parte deles (ex.: algo específico do Volt Digital numa
// categoria ligada aos três). Ver docs/validacao-prototipo.md §2.3 e §5.1.
import { z } from 'zod';
import { PRIORITIES, SUBJECT_DISTRIBUTION, TICKET_TYPES } from './enums.js';
import { slugify } from './slug.js';
import { subjectFormInputSchema } from './subjectFields.js';
/** Slug é gerado do nome, mas continua editável. Vazio na entrada = derivar. */
export const slugSchema = z
    .string()
    .trim()
    .max(80)
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Use apenas letras minúsculas, números e hífen');
const nameSchema = z.string().trim().min(2).max(120);
const descriptionSchema = z.string().trim().max(2000).optional().default('');
/** Aplica o slug derivado do nome quando o campo vier vazio. */
function withDerivedSlug(value) {
    return {
        ...value,
        slug: value.slug && value.slug.length > 0 ? value.slug : slugify(value.name),
    };
}
/* ── Sistemas ────────────────────────────────────────────────────────────── */
export const hexColorSchema = z
    .string()
    .trim()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Informe uma cor no formato #RRGGBB');
export const systemInputSchema = z
    .object({
    name: nameSchema,
    slug: slugSchema.optional(),
    /** Cor da bullet que identifica o sistema nas listas e nos gráficos. */
    color: hexColorSchema,
    description: descriptionSchema,
    active: z.boolean().default(true),
})
    .transform(withDerivedSlug);
export const SYSTEM_SORTABLE = [
    'name',
    'slug',
    'categoryCount',
    'subjectCount',
    'active',
    'createdAt',
];
/* ── Categorias ──────────────────────────────────────────────────────────── */
export const categoryInputSchema = z
    .object({
    name: nameSchema,
    slug: slugSchema.optional(),
    description: descriptionSchema,
    /** Sistemas em que a categoria aparece. Pelo menos um: sem isso os assuntos
     *  dela ficam inalcançáveis na abertura (ver painel de saúde, §6.3). */
    systemIds: z.array(z.string().uuid()).min(1, 'Escolha pelo menos um sistema'),
    active: z.boolean().default(true),
})
    .transform(withDerivedSlug);
export const CATEGORY_SORTABLE = [
    'name',
    'slug',
    'subjectCount',
    'active',
    'createdAt',
];
/* ── Assuntos ────────────────────────────────────────────────────────────── */
export const REQUIRED_FIELDS = [
    'anexo',
    'ocorrido_em',
    'usuarios_impactados',
    'protocolo_anterior',
];
export const subjectInputSchema = z
    .object({
    name: nameSchema,
    slug: slugSchema.optional(),
    categoryId: z.string().uuid(),
    /** Descrição mostrada ao cliente na lista de abertura. */
    description: descriptionSchema,
    visibleToClient: z.boolean().default(true),
    /** Vazio = todos os sistemas da categoria. Só restringe quando preenchido. */
    systemIds: z.array(z.string().uuid()).default([]),
    /** Nulo = cai no fallback global de `settings` (§6.1). Nunca trava o chamado. */
    areaId: z.string().uuid().nullable().default(null),
    slaPolicyId: z.string().uuid().nullable().default(null),
    useContractSla: z.boolean().default(false),
    defaultPriority: z.enum(PRIORITIES).default('media'),
    ticketType: z.enum(TICKET_TYPES).default('incidente'),
    distribution: z.enum(SUBJECT_DISTRIBUTION).default('herdar'),
    requiredFields: z.array(z.enum(REQUIRED_FIELDS)).default([]),
    /** Perguntas próprias deste assunto, na ordem em que aparecem na abertura.
     *  Ver subjectFields.ts. */
    form: subjectFormInputSchema,
    suggestedFirstReply: z.string().trim().max(4000).optional().default(''),
    active: z.boolean().default(true),
})
    .transform(withDerivedSlug);
export const SUBJECT_SORTABLE = [
    'name',
    'slug',
    'categoryName',
    'areaName',
    'slaPolicyName',
    'defaultPriority',
    'ticketCount',
    'active',
    'createdAt',
];
/* ── Saúde do catálogo (§6.3) ────────────────────────────────────────────── */
/**
 * Pendências de cadastro que não são erro de validação, mas quebram o sistema
 * mais adiante: categoria sem sistema deixa os assuntos dela inalcançáveis na
 * abertura, área sem ninguém disponível manda o chamado para uma fila que
 * ninguém olha.
 *
 * A ideia é o administrador ver isso antes, e não descobrir quando um chamado
 * cair no lugar errado.
 */
export const CATALOG_ISSUES = [
    'categoria_sem_sistema',
    'sistema_sem_assunto',
    'assunto_sem_area',
    'assunto_sem_politica',
    'area_sem_integrante',
    'projeto_sem_sistema',
    'cliente_sem_projeto',
    'empresa_sem_projeto',
];
export const CATALOG_ISSUE_LABEL = {
    categoria_sem_sistema: 'Categorias sem nenhum sistema',
    sistema_sem_assunto: 'Sistemas sem nenhum assunto ativo',
    assunto_sem_area: 'Assuntos sem área responsável',
    assunto_sem_politica: 'Assuntos sem política de prazo',
    area_sem_integrante: 'Áreas sem ninguém disponível',
    projeto_sem_sistema: 'Projetos sem nenhum sistema',
    cliente_sem_projeto: 'Clientes sem nenhum projeto',
    empresa_sem_projeto: 'Empresas sem nenhum projeto',
};
export const CATALOG_ISSUE_HINT = {
    categoria_sem_sistema: 'Os assuntos destas categorias não aparecem na abertura.',
    sistema_sem_assunto: 'Quem usa só este sistema não tem o que escolher.',
    assunto_sem_area: 'Vão todos para a área padrão do sistema.',
    assunto_sem_politica: 'Vão todos para a política padrão do sistema.',
    area_sem_integrante: 'Os chamados ficam numa fila que ninguém está vendo.',
    projeto_sem_sistema: 'Ninguém consegue abrir chamado neste projeto.',
    cliente_sem_projeto: 'Estas pessoas entram no portal e não conseguem abrir nada.',
    empresa_sem_projeto: 'Os usuários desta empresa não têm onde abrir chamado.',
};
/** Para onde a linha leva, para a tela resolver em um clique. */
export const CATALOG_ISSUE_PATH = {
    categoria_sem_sistema: '/admin/categorias',
    sistema_sem_assunto: '/admin/assuntos',
    assunto_sem_area: '/admin/assuntos',
    assunto_sem_politica: '/admin/assuntos',
    area_sem_integrante: '/admin/areas',
    projeto_sem_sistema: '/admin/projetos',
    cliente_sem_projeto: '/admin/usuarios',
    empresa_sem_projeto: '/admin/empresas',
};
